import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
  type ChartEvent,
  type ActiveElement,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import styles from "./Dashboard.module.css";
import {
  buildBatchTrendSeries,
  countTrendScoreCollision,
  formatDashboardChartTime,
  latestTrendPoint,
  representativeTrendCollisionAgentId,
  trendBatchKey,
} from "../utils/trendBatch";
import {
  caseEvaluationBasis,
  isSyntheticJudgeOnlyReference,
} from "../utils/evaluationReference";
import type {
  ModelComparisonGroup,
  ModelTrendPoint,
  PairDashboardItem,
  PairDashboardPayload,
} from "../services/metricsApi";
import { metricsApi } from "../services/metricsApi";

const EMPTY_DASHBOARD_ITEMS: PairDashboardItem[] = [];

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
);

export const CHART_MODEL_COLORS = ["#4079ED", "#5960f0", "#8b5cf6", "#0EA5E9", "#22A06B", "#E67E22"];
const TREND_COLORS = CHART_MODEL_COLORS;
const DASHBOARD_WINDOW_DAYS = 7;

function dashboardWindowStartMs(): number {
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - DASHBOARD_WINDOW_DAYS);
  start.setUTCHours(0, 0, 0, 0);
  return start.getTime();
}

function isWithinDashboardWindow(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return false;
  return ts >= dashboardWindowStartMs();
}

function createWinnerStarPlugin(groups: ModelComparisonGroup[], modelIds: string[]) {
  return {
    id: "winnerStarPlugin",
    afterDatasetsDraw(chart: ChartJS) {
      const { ctx } = chart;
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      if (!xScale || !yScale) return;

      groups.forEach((group, dataIndex) => {
        const winner = group.models.find((model) => model.isWinner);
        if (!winner || typeof winner.correctness !== "number") return;
        const datasetIndex = modelIds.indexOf(winner.agentId);
        if (datasetIndex < 0) return;
        const meta = chart.getDatasetMeta(datasetIndex);
        const bar = meta.data[dataIndex] as { x: number; y: number } | undefined;
        if (!bar) return;
        ctx.save();
        ctx.fillStyle = "#F2B705";
        ctx.font = "bold 14px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText("★", bar.x, bar.y - 6);
        ctx.restore();
      });
    },
  };
}

const Card: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}> = ({
  title,
  children,
  className,
  action,
}) => (
  <section className={`${styles.dashboardCard} ${className ?? ""}`.trim()}>
    <div className={styles.dashboardCard_header}>
      <h3 className={styles.dashboardCard_title}>{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

function isQualityScoreName(name: string): boolean {
  return /correctness|judge-only/i.test(name);
}

function pickCorrectness(
  scores: Record<string, number>,
  preferredMetric?: string | null,
): { name: string; value: number } | null {
  if (preferredMetric && typeof scores[preferredMetric] === "number") {
    return { name: preferredMetric, value: scores[preferredMetric] };
  }
  const entries = Object.entries(scores).filter(
    ([name, value]) => isQualityScoreName(name) && Number.isFinite(value),
  );
  if (entries.length === 0) return null;
  // Prefer verified correctness names over judge-only when both exist.
  entries.sort((a, b) => {
    const aJudge = /judge-only/i.test(a[0]) ? 1 : 0;
    const bJudge = /judge-only/i.test(b[0]) ? 1 : 0;
    if (aJudge !== bJudge) return aJudge - bJudge;
    return a[0].localeCompare(b[0]);
  });
  return { name: entries[0]![0], value: entries[0]![1] };
}

function toUnitScore(value: number): number {
  return value > 1 ? value / 100 : value;
}

/** Sub-cent LLM runs need more than 4 decimals (e.g. 0.000014 → 0.0000 with toFixed(4)). */
function formatCostUsd(value: number): string {
  if (value >= 0.01) return value.toFixed(4);
  if (value >= 0.0001) return value.toFixed(6);
  return value.toFixed(8);
}

function itemChartLabel(itemIndex: number): string {
  return `Item ${itemIndex + 1}`;
}

function dedupeTrendSeriesByModel(
  series: PairDashboardPayload["modelTrends"],
  preferredAgentId: string,
): PairDashboardPayload["modelTrends"] {
  const byModel = new Map<string, PairDashboardPayload["modelTrends"][number]>();
  for (const entry of series) {
    const modelKey = (entry.model || entry.agentName || entry.agentId).trim().toLowerCase();
    const existing = byModel.get(modelKey);
    if (!existing) {
      byModel.set(modelKey, entry);
      continue;
    }
    if (entry.agentId === preferredAgentId) {
      byModel.set(modelKey, entry);
      continue;
    }
    if (existing.agentId !== preferredAgentId && entry.points.length > existing.points.length) {
      byModel.set(modelKey, entry);
    }
  }
  return Array.from(byModel.values()).sort((a, b) =>
    (a.model || a.agentId).localeCompare(b.model || b.agentId),
  );
}

function trendPointValue(point: ModelTrendPoint | undefined): number | null {
  if (typeof point?.correctness === "number") return toUnitScore(point.correctness);
  if (availabilityState(point?.availability) === "down") return 0;
  return null;
}

const findTrendPoint = (
  entry: PairDashboardPayload["modelTrends"][number],
  tick: { key: string; label: string; createdAt: string | null; runId?: string | null },
) =>
  entry.points.find((point) => trendBatchKey(point) === tick.key) ??
  (tick.runId ? entry.points.find((point) => point.runId === tick.runId) : undefined) ??
  entry.points.find((point) => point.label === tick.label) ??
  (tick.createdAt ? entry.points.find((point) => point.createdAt === tick.createdAt) : undefined);

/** Label exact same-batch/same-score points without changing their values. */
function createTrendCollisionPlugin() {
  return {
    id: "trendPointCollisions",
    afterDatasetsDraw(chart: ChartJS) {
      const groups = new Map<string, Array<{ x: number; y: number }>>();
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        dataset.data.forEach((value, dataIndex) => {
          if (typeof value !== "number") return;
          const point = chart.getDatasetMeta(datasetIndex).data[dataIndex] as
            | { x: number; y: number }
            | undefined;
          if (!point) return;
          const key = `${dataIndex}:${value}`;
          groups.set(key, [...(groups.get(key) ?? []), point]);
        });
      });
      for (const points of Array.from(groups.values())) {
        if (points.length < 2) continue;
        const x = points.reduce((sum, point) => sum + point.x, 0) / points.length;
        const y = Math.min(...points.map((point) => point.y));
        const label = `×${points.length}`;
        const { ctx } = chart;
        ctx.save();
        ctx.font = "600 10px system-ui, sans-serif";
        const width = ctx.measureText(label).width + 8;
        const boxHeight = 16;
        // Keep labels above markers; layout.padding.top gives room past chartArea.
        // Only flip below when the label would leave the canvas entirely.
        let boxTop = y - 22;
        if (boxTop < 2) {
          boxTop = y + 10;
        }
        ctx.fillStyle = "#151D48";
        ctx.fillRect(x - width / 2, boxTop, width, boxHeight);
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, x, boxTop + boxHeight / 2);
        ctx.restore();
      }
    },
  };
}

type TrendClickCandidate = {
  agentId: string;
  runId: string;
  label: string;
  score: number;
  color: string;
};

function scoreKey(value: number): string {
  return toUnitScore(value).toFixed(3);
}

function resolveTrendClickCandidates(
  chart: ChartJS,
  event: ChartEvent,
  trendSeries: PairDashboardPayload["modelTrends"],
  timeline: Array<{ key: string; label: string; createdAt: string | null; runId: string | null }>,
): { tickIndex: number; score: number; candidates: TrendClickCandidate[]; clientX: number; clientY: number } | null {
  const nativeEvent = event.native;
  if (!nativeEvent || !("clientX" in nativeEvent)) return null;
  const nearest = chart.getElementsAtEventForMode(
    nativeEvent as unknown as Event,
    "nearest",
    { intersect: false },
    false,
  ) as ActiveElement[];
  if (nearest.length === 0) return null;
  const primary = nearest[0]!;
  const tick = timeline[primary.index];
  if (!tick) return null;
  const primaryEntry = trendSeries[primary.datasetIndex];
  const primaryPoint = primaryEntry ? findTrendPoint(primaryEntry, tick) : undefined;
  const primaryScore =
    typeof primaryPoint?.correctness === "number"
      ? toUnitScore(primaryPoint.correctness)
      : availabilityState(primaryPoint?.availability) === "down"
        ? 0
        : null;
  if (primaryScore == null) return null;

  const targetKey = scoreKey(primaryScore);
  const candidates: TrendClickCandidate[] = [];
  trendSeries.forEach((entry, datasetIndex) => {
    const point = findTrendPoint(entry, tick);
    if (!point?.runId) return;
    const score =
      typeof point.correctness === "number"
        ? toUnitScore(point.correctness)
        : availabilityState(point.availability) === "down"
          ? 0
          : null;
    if (score == null || scoreKey(score) !== targetKey) return;
    candidates.push({
      agentId: entry.agentId,
      runId: point.runId,
      label: entry.model || entry.agentName || entry.agentId,
      score,
      color: TREND_COLORS[datasetIndex % TREND_COLORS.length]!,
    });
  });
  if (candidates.length === 0) return null;
  return {
    tickIndex: primary.index,
    score: primaryScore,
    candidates,
    clientX: (nativeEvent as MouseEvent).clientX,
    clientY: (nativeEvent as MouseEvent).clientY,
  };
}


function itemDotColor(item: PairDashboardItem, unit: number | null): string {
  if (
    item.status === "failed" ||
    (typeof item.scores.success === "number" && item.scores.success < 0.5)
  ) {
    return "#DC2626";
  }
  if (unit == null) return "#94A3B8";
  if (unit >= 0.75) return "#22A06B";
  if (unit >= 0.5) return "#E67E22";
  return "#DC2626";
}

function useIsCompactViewport(maxWidth = 900): boolean {
  const [compact, setCompact] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches : false,
  );

  React.useEffect(() => {
    const media = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = () => setCompact(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [maxWidth]);

  return compact;
}

type AvailabilityState = "up" | "degraded" | "down" | "unknown";

function availabilityState(value: number | null | undefined): AvailabilityState {
  if (typeof value !== "number" || Number.isNaN(value)) return "unknown";
  const unit = toUnitScore(value);
  if (unit <= 0.05) return "down";
  if (unit < 0.99) return "degraded";
  return "up";
}

function itemCorrectness(
  item: PairDashboardItem,
  preferredMetric?: string | null,
): number | null {
  const fromScores = pickCorrectness(item.scores, preferredMetric)?.value;
  if (typeof fromScores === "number") return fromScores;
  if (item.evaluation && Number.isFinite(item.evaluation.score)) {
    return item.evaluation.score;
  }
  return null;
}

function maskExplicitText(value: string | null | undefined): string {
  if (!value) return "—";
  const terms = [
    "fuck",
    "fucking",
    "shit",
    "bitch",
    "asshole",
    "bastard",
    "damn",
  ];
  return terms.reduce((text, term) => {
    const pattern = new RegExp(`\\b${term}\\b`, "gi");
    return text.replace(pattern, "");
  }, value)
    .replace(/\s{2,}/g, " ")
    .trim() || "—";
}

const gaugeSegmentColor = (q: number): string => {
  if (q <= 40) return "#F56888";
  if (q <= 60) return "#EAA5B8";
  if (q <= 75) return "#ECD32E";
  return "#6CD679";
};

const gaugeStatusLabel = (q: number): string => {
  if (q >= 90) return "Excellent";
  if (q >= 75) return "Good";
  if (q >= 60) return "Fair";
  return "Needs Work";
};

const gaugeBadgeStyles = (
  color: string,
): { border: string; background: string; text: string } => {
  switch (color) {
    case "#F56888":
      return { border: "#F56888", background: "#FFF0F3", text: "#DC2626" };
    case "#EAA5B8":
      return { border: "#EAA5B8", background: "#FFF5F8", text: "#C0506E" };
    case "#ECD32E":
      return { border: "#ECD32E", background: "#FFFCE5", text: "#A68B09" };
    default:
      return { border: "#22D58A", background: "#EAFFF3", text: "#10B981" };
  }
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const DetailsCard: React.FC<{ dashboard: PairDashboardPayload; loading?: boolean }> = ({
  dashboard,
  loading,
}) => {
  const rows = [
    { label: "Agent", value: dashboard.agentName || dashboard.agentId },
    { label: "Dataset", value: dashboard.datasetName || dashboard.datasetId },
    { label: "Model", value: dashboard.model || "—" },
    {
      label: "Latest run",
      value: dashboard.runCompletedAt
        ? new Date(dashboard.runCompletedAt).toLocaleString()
        : "—",
    },
  ];

  return (
    <Card title="Run details">
      {loading ? (
        <p className={styles.dashboardEmptyText}>Loading run details...</p>
      ) : (
        <dl className={styles.detailsList}>
          {rows.map((row) => (
            <div key={row.label} className={styles.detailsRow}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
};

const CorrectnessGaugeCard: React.FC<{ dashboard: PairDashboardPayload; loading?: boolean }> = ({
  dashboard,
  loading,
}) => {
  const picked = pickCorrectness(dashboard.aggregates, dashboard.correctnessMetric);
  const fromItems = dashboard.items
    .map((item) => {
      const score = itemCorrectness(item, dashboard.correctnessMetric);
      if (score != null) return toUnitScore(score);
      if (item.status === "failed") return 0;
      return null;
    })
    .filter((value): value is number => value != null);

  let avgUnit: number | null = null;
  if (picked != null) {
    avgUnit = toUnitScore(picked.value);
  } else if (fromItems.length > 0) {
    avgUnit = fromItems.reduce((sum, value) => sum + value, 0) / fromItems.length;
  }

  const targetQuality =
    avgUnit == null ? null : Math.max(0, Math.min(100, Math.round(avgUnit * 100)));

  const [displayQuality, setDisplayQuality] = React.useState(0);
  const animRef = React.useRef<number | null>(null);
  const prevTarget = React.useRef(0);

  React.useEffect(() => {
    if (animRef.current !== null) cancelAnimationFrame(animRef.current);
    const from = prevTarget.current;
    const to = targetQuality ?? 0;
    const duration = 900;
    const start = performance.now();

    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      const current = Math.round(from + (to - from) * eased);
      setDisplayQuality(current);
      if (progress < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
        prevTarget.current = to;
      }
    };
    animRef.current = requestAnimationFrame(step);
    return () => {
      if (animRef.current !== null) cancelAnimationFrame(animRef.current);
    };
  }, [targetQuality]);

  const cx = 100;
  const cy = 100;
  const r = 76;
  const stroke = 12;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toXY = (deg: number) => ({
    x: cx + r * Math.cos(toRad(deg)),
    y: cy - r * Math.sin(toRad(deg)),
  });
  const arc = (startDeg: number, endDeg: number) => {
    const s = toXY(startDeg);
    const e = toXY(endDeg);
    const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const markerAngle = 180 - (displayQuality / 100) * 180;
  const gap = 2;
  const marker = toXY(markerAngle);
  const dotColor = gaugeSegmentColor(displayQuality);
  const status = targetQuality == null ? "Not scored" : gaugeStatusLabel(targetQuality);
  const badge = gaugeBadgeStyles(gaugeSegmentColor(targetQuality ?? 0));

  return (
    <Card title="Correctness score" className={styles.gaugeCardCompact}>
      {loading ? (
        <p className={styles.dashboardEmptyText}>Loading score...</p>
      ) : (
      <div className={`${styles.overallQualityWrap} ${styles.gaugeCompactWrap}`}>
        <div className={`${styles.gaugeWrap} ${styles.gaugeWrapCompact}`}>
          <svg viewBox="0 0 200 120" className={`${styles.gaugeSvg} ${styles.gaugeSvgCompact}`} aria-hidden="true">
            <path d={arc(180, 108 + gap)} fill="none" stroke="#F56888" strokeWidth={stroke} strokeLinecap="round" />
            <path d={arc(100 - gap, 74 + gap)} fill="none" stroke="#EAA5B8" strokeWidth={stroke} strokeLinecap="round" />
            <path d={arc(66 - gap, 52 + gap)} fill="none" stroke="#ECD32E" strokeWidth={stroke} strokeLinecap="round" />
            <path d={arc(44 - gap, 2)} fill="none" stroke="#6CD679" strokeWidth={stroke} strokeLinecap="round" />
            <circle cx={marker.x} cy={marker.y} r="6.5" fill="#FFFFFF" stroke={dotColor} strokeWidth="4" />
          </svg>
          <div className={styles.gaugeCenter}>
            <div className={styles.gaugeValue}>
              {targetQuality == null ? "N/A" : `${displayQuality}%`}
            </div>
            <div className={styles.gaugeSubtitle}>
              {dashboard.correctnessMetric || "correctness"}
            </div>
            <div
              className={styles.gaugeTag}
              style={{
                borderColor: badge.border,
                backgroundColor: badge.background,
                color: badge.text,
              }}
            >
              {status}
            </div>
          </div>
        </div>
      </div>
      )}
    </Card>
  );
};

const CostPerRunCard: React.FC<{
  pairId: string;
  runId: string | null;
  initialCostUsd: number | null;
  loading?: boolean;
}> = ({ runId, initialCostUsd, loading = false }) => {
  const hasCost = typeof initialCostUsd === "number" && Number.isFinite(initialCostUsd);
  return (
    <Card title="Cost per run" className={styles.costCardCompact}>
      <div className={styles.costCardBody}>
        <div className={styles.costCardValue}>
          {!runId ? "—" : loading ? "…" : hasCost ? `$${formatCostUsd(initialCostUsd!)}` : "—"}
        </div>
        <p className={styles.costCardHint}>
          {!runId
            ? "Select a run on the trend chart"
            : loading
              ? "Loading trace costs..."
              : hasCost
                ? initialCostUsd! > 0
                  ? "Selected run total from Langfuse traces"
                  : "Run completed with no recorded model cost"
                : "No priced trace costs recorded"}
        </p>
      </div>
    </Card>
  );
};


function latestRunKeyForSeries(
  entry: PairDashboardPayload["modelTrends"][number],
): string | null {
  const latest = latestTrendPoint(entry.points);
  return latest ? trendBatchKey(latest) : null;
}

const CorrectnessTrendByModelCard: React.FC<{
  dashboard: PairDashboardPayload;
  selectedAgentId?: string | null;
  selectedRunId?: string | null;
  onSelectRun?: (agentId: string, runId: string) => void;
}> = ({ dashboard, selectedAgentId, selectedRunId, onSelectRun }) => {
  const compact = useIsCompactViewport();
  const chartRef = React.useRef<ChartJS<"line">>(null);
  const [clusterPicker, setClusterPicker] = React.useState<{
    score: number;
    label: string;
    candidates: TrendClickCandidate[];
  } | null>(null);
  const preferredAgentId = selectedAgentId ?? dashboard.agentId;
  const baseSeriesRaw =
    dashboard.modelTrends.length > 0
      ? dashboard.modelTrends
      : [
          {
            agentId: dashboard.agentId,
            agentName: dashboard.agentName,
            model: dashboard.model,
            points: dashboard.experimentScores.map((point) => ({
              runId: point.runId,
              comparisonId: null,
              label: point.label,
              createdAt: null as string | null,
              correctness: point.correctness,
              availability: point.availability,
              selected: point.selected,
            })),
          },
        ];
  const baseSeries = dedupeTrendSeriesByModel(baseSeriesRaw, preferredAgentId);
  const filteredSeries = baseSeries.map((entry) => ({
    ...entry,
    points: entry.points.filter((point) => isWithinDashboardWindow(point.createdAt)),
  }));
  const { series: trendSeries, timeline } = buildBatchTrendSeries(filteredSeries);
  const collisionPlugin = React.useMemo(() => createTrendCollisionPlugin(), []);
  const latestRunKeyByAgent = new Map(
    trendSeries.map((entry) => [entry.agentId, latestRunKeyForSeries(entry)]),
  );

  const isLatestDot = (agentId: string, tickKey: string) =>
    latestRunKeyByAgent.get(agentId) === tickKey;

  const isSelectedDot = (
    entry: PairDashboardPayload["modelTrends"][number],
    tickKey: string,
  ) =>
    entry.agentId === selectedAgentId &&
    Boolean(
      selectedRunId &&
        entry.points.some(
          (point) => trendBatchKey(point) === tickKey && point.runId === selectedRunId,
        ),
    );

  const handleTrendClick = (event: ChartEvent) => {
    if (!onSelectRun) return;
    const chart = chartRef.current;
    if (!chart) return;
    const result = resolveTrendClickCandidates(chart, event, trendSeries, timeline);
    if (!result) return;
    const tick = timeline[result.tickIndex];
    if (!tick) return;
    // Older runs stay hover-only; only each model's latest point opens panels.
    const candidates = result.candidates.filter((candidate) =>
      isLatestDot(candidate.agentId, tick.key),
    );
    if (candidates.length === 0) return;
    if (candidates.length === 1) {
      const only = candidates[0]!;
      setClusterPicker(null);
      onSelectRun(only.agentId, only.runId);
      return;
    }
    chart.setActiveElements([]);
    chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
    chart.update("none");
    setClusterPicker({
      score: result.score,
      label: tick.label ?? "",
      candidates,
    });
  };

  const isCollisionVisible = (
    entry: PairDashboardPayload["modelTrends"][number],
    tickKey: string,
    rawScore: number | null | undefined,
  ) => {
    if (typeof rawScore !== "number") return true;
    if (countTrendScoreCollision(trendSeries, tickKey, rawScore) <= 1) return true;
    return (
      representativeTrendCollisionAgentId(
        trendSeries,
        tickKey,
        rawScore,
        selectedAgentId,
      ) === entry.agentId
    );
  };

  const renderClusterPicker = () => {
    if (!clusterPicker || !onSelectRun) return null;
    return (
      <div className={styles.trendBatchPicker} role="listbox" aria-label="Choose model for this score">
        <div className={styles.trendBatchPickerHeader}>
          <strong>
            {clusterPicker.candidates.length} models at {clusterPicker.score.toFixed(3)}
          </strong>
          {clusterPicker.label ? <span>({clusterPicker.label})</span> : null}
          <span>— pick one to open</span>
          <button
            type="button"
            className={styles.trendBatchPickerClose}
            onClick={() => setClusterPicker(null)}
            aria-label="Close model picker"
          >
            ×
          </button>
        </div>
        <div className={styles.trendBatchPickerList}>
          {clusterPicker.candidates.map((candidate) => (
            <button
              key={`${candidate.agentId}:${candidate.runId}`}
              type="button"
              role="option"
              className={styles.trendBatchPickerBtn}
              onClick={() => {
                setClusterPicker(null);
                onSelectRun(candidate.agentId, candidate.runId);
              }}
            >
              <span
                className={styles.trendBatchPickerSwatch}
                style={{ backgroundColor: candidate.color }}
                aria-hidden
              />
              <span className={styles.trendBatchPickerName}>{candidate.label}</span>
              <span className={styles.trendBatchPickerScore}>{candidate.score.toFixed(3)}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card title="Correctness trend by model">
      <div className={styles.trendChartToolbar}>
        <span className={styles.trendChartToolbarHint}>
          Hover any point for details; click a latest-run (filled) point to open
        </span>
      </div>
      {renderClusterPicker()}
      <div className={`${styles.chartCanvasWrapLarge} ${styles.trendChartCanvas}`}>
        {renderTrendChart()}
      </div>
    </Card>
  );

  function renderTrendChart() {
    return (
        <Line
          ref={chartRef}
          plugins={[collisionPlugin]}
          data={{
            labels: timeline.map((point) => point.label),
            datasets: trendSeries.map((entry, index) => {
              const color = TREND_COLORS[index % TREND_COLORS.length]!;
              const byRun = new Map(entry.points.map((point) => [trendBatchKey(point), point]));
              const values = timeline.map((tick) => {
                const point = byRun.get(tick.key);
                if (typeof point?.correctness === "number") return toUnitScore(point.correctness);
                if (availabilityState(point?.availability) === "down") return 0;
                return null;
              });
              const pointColors = timeline.map((tick) => {
                if (isSelectedDot(entry, tick.key)) return color;
                if (isLatestDot(entry.agentId, tick.key)) return color;
                return "#FFFFFF";
              });
              const borderColors = timeline.map((tick) => {
                if (isSelectedDot(entry, tick.key)) return "#151D48";
                return color;
              });
              const radii = timeline.map((tick) => {
                const point = byRun.get(tick.key);
                if (!point || !isCollisionVisible(entry, tick.key, point.correctness)) return 0;
                if (isSelectedDot(entry, tick.key)) return 8;
                if (isLatestDot(entry.agentId, tick.key)) return 6;
                return 5;
              });
              const borderWidths = timeline.map((tick) =>
                isSelectedDot(entry, tick.key) ? 3 : 2,
              );
              const hitRadii = timeline.map((tick) => (byRun.get(tick.key) ? 22 : 0));
              const pointStyles = timeline.map((tick) => {
                const value = trendPointValue(byRun.get(tick.key));
                if (value == null) return "circle";
                const rawScore = byRun.get(tick.key)?.correctness;
                const collisions =
                  typeof rawScore === "number"
                    ? countTrendScoreCollision(trendSeries, tick.key, rawScore)
                    : 0;
                return collisions > 1 ? "rectRot" : "circle";
              });
              return {
                label: entry.model || entry.agentName || entry.agentId,
                data: values,
                borderColor: color,
                backgroundColor: color,
                fill: false,
                tension: 0.25,
                pointRadius: radii,
                pointHoverRadius: radii.map((radius) => (radius === 0 ? 0 : radius + 1)),
                pointHitRadius: hitRadii,
                pointBackgroundColor: pointColors,
                pointBorderColor: borderColors,
                pointBorderWidth: borderWidths,
                pointStyle: pointStyles,
                borderWidth: entry.agentId === selectedAgentId ? 2.75 : 2,
                spanGaps: true,
              };
            }),
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            animations: { colors: false },
            transitions: { active: { animation: { duration: 0 } } },
            clip: false,
            layout: { padding: { top: 40, right: 28, left: 10, bottom: 28 } },
            interaction: {
              mode: "nearest",
              intersect: false,
              axis: "xy",
            },
            onClick: (event) => handleTrendClick(event),
            onHover: (event, elements) => {
              const target = event.native?.target as HTMLElement | undefined;
              if (!target) return;
              if (!onSelectRun || elements.length === 0) {
                target.style.cursor = "default";
                return;
              }
              const clickable = elements.some((el) => {
                const tick = timeline[el.index];
                const entry = trendSeries[el.datasetIndex];
                if (!tick || !entry) return false;
                const point = findTrendPoint(entry, tick);
                return Boolean(point?.runId) && isLatestDot(entry.agentId, tick.key);
              });
              target.style.cursor = clickable ? "pointer" : "default";
            },
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: {
                  boxWidth: compact ? 10 : 12,
                  font: { size: compact ? 10 : 12 },
                  padding: compact ? 8 : 12,
                },
              },
              tooltip: {
                enabled: !clusterPicker,
                mode: "nearest",
                intersect: false,
                axis: "xy",
                // Overlapping diamonds yield multiple tooltip items; we list
                // same-score peers ourselves, so keep a single item.
                filter: (_item, index) => index === 0,
                callbacks: {
                  title: (items) => {
                    const idx = items[0]?.dataIndex ?? 0;
                    return timeline[idx]?.label ?? "";
                  },
                  label: (ctx) => {
                    const tick = timeline[ctx.dataIndex];
                    const targetY = ctx.parsed?.y;
                    if (!tick || typeof targetY !== "number") return "";
                    const target = scoreKey(targetY);
                    const lines: string[] = [];
                    for (const entry of trendSeries) {
                      const point = findTrendPoint(entry, tick);
                      const score = trendPointValue(point);
                      if (score == null || scoreKey(score) !== target) continue;
                      const modelLabel = entry.model || entry.agentName || entry.agentId;
                      const outage =
                        point?.correctness == null
                        && availabilityState(point?.availability) === "down";
                      lines.push(
                        outage
                          ? `${modelLabel}: 0.000 (outage)`
                          : `${modelLabel}: ${score.toFixed(3)}`,
                      );
                    }
                    return lines.length > 0 ? lines : "";
                  },
                  afterBody: (items) => {
                    const item = items[0];
                    if (!item) return [];
                    const tick = timeline[item.dataIndex];
                    const targetY = item.parsed?.y;
                    if (!tick || typeof targetY !== "number") return [];
                    const target = scoreKey(targetY);
                    let sameScore = 0;
                    for (const entry of trendSeries) {
                      const score = trendPointValue(findTrendPoint(entry, tick));
                      if (score != null && scoreKey(score) === target) sameScore += 1;
                    }
                    if (sameScore < 2) return [];
                    return ["", `${sameScore} models share ${Number(target).toFixed(3)}`];
                  },
                  footer: (items) => {
                    if (!onSelectRun || items.length === 0) return "";
                    const item = items[0]!;
                    const tick = timeline[item.dataIndex];
                    const targetY = item.parsed?.y;
                    if (!tick || typeof targetY !== "number") return "";
                    const target = scoreKey(targetY);
                    let withRun = 0;
                    for (const entry of trendSeries) {
                      const point = findTrendPoint(entry, tick);
                      const score = trendPointValue(point);
                      if (score == null || scoreKey(score) !== target) continue;
                      if (point?.runId) withRun += 1;
                    }
                    if (withRun === 0) return "No run linked for this point";
                    if (withRun === 1) return "Click to open run details";
                    return "Click to choose which model to open";
                  },
                },
              },
            },
            scales: {
              x: {
                offset: true,
                grid: { display: false },
                ticks: {
                  display: true,
                  color: "#7A7A92",
                  maxRotation: compact ? 60 : 45,
                  minRotation: compact ? 35 : 0,
                  autoSkip: true,
                  maxTicksLimit: compact ? 4 : 8,
                  font: { size: compact ? 9 : 11 },
                },
                border: { display: false },
              },
              y: {
                beginAtZero: true,
                min: 0,
                max: 1,
                grid: { color: "rgba(89, 96, 240, 0.14)" },
                ticks: {
                  color: "#7A7A92",
                  stepSize: 0.2,
                  font: { size: compact ? 9 : 11 },
                  callback: (v) => {
                    const n = typeof v === "number" ? v : Number(v);
                    if (!Number.isFinite(n) || n < -0.001 || n > 1.001) {
                      return "";
                    }
                    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
                  },
                },
              },
            },
          }}
        />
    );
  }
};

const ModelComparisonCard: React.FC<{ comparisons: ModelComparisonGroup[] }> = ({ comparisons }) => {
  const compact = useIsCompactViewport();
  const groups = [...comparisons]
    .filter((group) => isWithinDashboardWindow(group.createdAt))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const modelIds = Array.from(
    new Set(groups.flatMap((group) => group.models.map((model) => model.agentId))),
  );
  const winnerStarPlugin = React.useMemo(
    () => createWinnerStarPlugin(groups, modelIds),
    [groups, modelIds],
  );

  if (groups.length === 0) {
    return (
      <Card title="Model comparison by benchmark pass" className={styles.fullWidthCard}>
        <p className={styles.dashboardEmptyText}>
          No model comparison yet. Run all demo agents together:{" "}
          <code>npm run synthetic:banking-finance-qa-demo:run:compare</code> (from{" "}
          <code>server/</code>, with API keys set). That runs 4 models on the 4 demo cases and
          populates this chart. Re-run with <code>--passes 2</code> for more benchmark bars.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Model comparison by benchmark pass" className={styles.fullWidthCard}>
      <div className={styles.chartCanvasWrapLarge}>
        <Bar
          plugins={[winnerStarPlugin]}
          data={{
            labels: groups.map((group) =>
              formatDashboardChartTime(group.createdAt, new Date(group.createdAt).toLocaleString()),
            ),
            datasets: modelIds.map((agentId, index) => {
              const modelName =
                groups.flatMap((group) => group.models).find((model) => model.agentId === agentId)
                  ?.model || agentId;
              return {
                label: modelName,
                data: groups.map((group) => {
                  const model = group.models.find((entry) => entry.agentId === agentId);
                  return typeof model?.correctness === "number" ? toUnitScore(model.correctness) : null;
                }),
                backgroundColor: CHART_MODEL_COLORS[index % CHART_MODEL_COLORS.length],
                borderColor: groups.map((group) =>
                  group.models.find((entry) => entry.agentId === agentId)?.isWinner ? "#F2B705" : "transparent",
                ),
                borderWidth: groups.map((group) =>
                  group.models.find((entry) => entry.agentId === agentId)?.isWinner ? 3 : 0,
                ),
              };
            }),
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            // Headroom so winner ★ above a 1.0 bar is not clipped by the canvas edge.
            layout: { padding: { top: 22 } },
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: {
                  boxWidth: compact ? 10 : 12,
                  font: { size: compact ? 10 : 12 },
                  padding: compact ? 8 : 12,
                },
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const group = groups[context.dataIndex];
                    const agentId = modelIds[context.datasetIndex];
                    const model = group?.models.find((entry) => entry.agentId === agentId);
                    if (!model || typeof model.correctness !== "number") return "not scored";
                    const score = toUnitScore(model.correctness).toFixed(3);
                    return model.isWinner
                      ? `★ ${model.model || model.agentId}: ${score} (winner)`
                      : `${model.model || model.agentId}: ${score}`;
                  },
                  afterLabel: (context) => {
                    const group = groups[context.dataIndex];
                    const agentId = modelIds[context.datasetIndex];
                    const model = group?.models.find((entry) => entry.agentId === agentId);
                    if (!model) return "";
                    const availability =
                      model.availability == null
                        ? "not recorded"
                        : `${Math.round(toUnitScore(model.availability) * 100)}%`;
                    return `Availability: ${availability}`;
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                min: 0,
                max: 1,
                ticks: { font: { size: compact ? 9 : 11 } },
              },
              x: {
                grid: { display: false },
                ticks: {
                  maxRotation: compact ? 55 : 0,
                  minRotation: compact ? 30 : 0,
                  autoSkip: true,
                  maxTicksLimit: compact ? 3 : 8,
                  font: { size: compact ? 9 : 11 },
                },
              },
            },
          }}
        />
      </div>
    </Card>
  );
};

const RunItemsDotChartBody: React.FC<{
  pairId: string;
  runId: string | null;
  correctnessMetric: string | null;
  datasetName: string | null;
  initialItems?: PairDashboardItem[];
}> = ({ pairId, runId, correctnessMetric, datasetName, initialItems = [] }) => {
  const compact = useIsCompactViewport();
  const [rows, setRows] = React.useState<PairDashboardItem[]>(initialItems);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setRows(initialItems);
  }, [initialItems]);

  React.useEffect(() => {
    if (!runId) {
      setRows([]);
      return;
    }
    if (initialItems.length > 0) {
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    void metricsApi
      .getPairRunItems(pairId, runId, 1, 50)
      .then((res) => {
        if (mounted) setRows(res.items);
      })
      .catch(() => {
        if (mounted) setRows(initialItems);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [pairId, runId, initialItems]);

  const sorted = [...rows].sort((a, b) => a.itemIndex - b.itemIndex);
  const labels = sorted.map((row) => itemChartLabel(row.itemIndex));
  const values = sorted.map((row) => {
    const score = itemCorrectness(row, correctnessMetric);
    if (score == null) {
      if (row.status === "failed") return 0;
      return null;
    }
    return toUnitScore(score);
  });
  const colors = sorted.map((row, index) => itemDotColor(row, values[index] ?? null));
  const scoredCount = values.filter((value) => value != null).length;

  if (!runId) {
    return <p className={styles.dashboardEmptyText}>No run selected.</p>;
  }

  if (loading && sorted.length === 0) {
    return <p className={styles.dashboardEmptyText}>Loading item scores...</p>;
  }

  if (sorted.length === 0) {
    return <p className={styles.dashboardEmptyText}>No scored items yet.</p>;
  }

  if (scoredCount === 0) {
    return (
      <p className={styles.dashboardEmptyText}>
        {sorted.length} item{sorted.length === 1 ? "" : "s"} found, but none have a quality score yet.
      </p>
    );
  }

  return (
    <>
      <p className={styles.costCardHint}>Per-item correctness for the selected run</p>
      <div className={`${styles.chartCanvasWrapLarge} ${styles.runItemsChartCanvas}`}>
        <Line
          data={{
            labels,
            datasets: [
              {
                label: correctnessMetric || "correctness",
                data: values,
                borderColor: "#4079ED",
                backgroundColor: "#4079ED",
                showLine: true,
                tension: 0.25,
                pointRadius: values.map((value) => (value == null ? 0 : 8)),
                pointHoverRadius: 10,
                pointBackgroundColor: colors,
                pointBorderColor: "#FFFFFF",
                pointBorderWidth: 2,
                spanGaps: true,
                clip: false,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            clip: false,
            layout: { padding: { top: 28, right: 22, left: 22, bottom: 14 } },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const row = sorted[ctx.dataIndex];
                    if (!row) return "";
                    const score = values[ctx.dataIndex];
                    if (score == null) {
                      return row.status === "failed" ? "Run Failed" : "Not scored";
                    }
                    return `Correctness: ${score.toFixed(3)}`;
                  },
                },
              },
            },
            scales: {
              x: {
                offset: true,
                grid: { display: false },
                ticks: {
                  color: "#7A7A92",
                  maxRotation: compact ? 45 : 0,
                  font: { size: compact ? 9 : 11 },
                },
              },
              y: {
                beginAtZero: true,
                min: 0,
                max: 1,
                grace: "12%",
                grid: { color: "rgba(89, 96, 240, 0.14)" },
                ticks: {
                  color: "#7A7A92",
                  stepSize: 0.25,
                  font: { size: compact ? 9 : 11 },
                },
              },
            },
          }}
        />
      </div>
    </>
  );
};

const RunItemScoresCard: React.FC<{
  pairId: string;
  runId: string | null;
  correctnessMetric: string | null;
  datasetName: string | null;
  initialItems?: PairDashboardItem[];
  loading?: boolean;
}> = ({ pairId, runId, correctnessMetric, datasetName, initialItems = [], loading }) => (
  <Card title="Run item scores">
    {loading ? (
      <p className={styles.dashboardEmptyText}>Loading item scores...</p>
    ) : !runId ? (
      <p className={styles.dashboardEmptyText}>Select a model&apos;s latest run on the trend chart.</p>
    ) : (
      <RunItemsDotChartBody
        pairId={pairId}
        runId={runId}
        correctnessMetric={correctnessMetric}
        datasetName={datasetName}
        initialItems={initialItems}
      />
    )}
  </Card>
);

function fullItemText(
  value: unknown,
  fallback: string | null | undefined,
  preferredKeys: string[] = ["question", "output", "answer", "text"],
): string {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of preferredKeys) {
      const candidate = record[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      // Fall through to the API preview.
    }
  }
  return fallback?.trim() || "—";
}

const EvaluationTextSection: React.FC<{ title: string; value: string }> = ({ title, value }) => (
  <section className={styles.evaluationSection}>
    <h3>{title}</h3>
    <pre>{value}</pre>
  </section>
);

const RunItemsCard: React.FC<{
  pairId: string;
  runId: string | null;
  correctnessMetric: string | null;
  itemTotal: number;
  initialItems?: PairDashboardItem[];
  loading?: boolean;
}> = ({
  pairId,
  runId,
  correctnessMetric,
  itemTotal,
  initialItems = [],
  loading = false,
}) => {
  const [rows, setRows] = React.useState<PairDashboardItem[]>(initialItems);
  const [selectedItem, setSelectedItem] = React.useState<PairDashboardItem | null>(null);
  const evaluationCloseRef = React.useRef<HTMLButtonElement | null>(null);
  const [page, setPage] = React.useState(1);
  const [fetching, setFetching] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadedTotal, setLoadedTotal] = React.useState(initialItems.length);
  const sentinelRef = React.useRef<HTMLTableRowElement | null>(null);
  const scrollRootRef = React.useRef<HTMLDivElement | null>(null);
  const metric = correctnessMetric;
  const seededFromParent = initialItems.length > 0;

  React.useEffect(() => {
    if (!selectedItem) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedItem(null);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    evaluationCloseRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedItem]);

  React.useEffect(() => {
    setRows(initialItems);
    setPage(1);
    setLoadedTotal(
      initialItems.length > 0 ? Math.max(itemTotal, initialItems.length) : itemTotal || 0,
    );
    // Parent loads up to 100 items; only paginate when we have no seed yet.
    setHasMore(initialItems.length === 0 && itemTotal > 0);
  }, [pairId, runId, initialItems, itemTotal]);

  React.useEffect(() => {
    if (!runId || seededFromParent) return;
    let mounted = true;
    const load = async () => {
      setFetching(true);
      try {
        const res = await metricsApi.getPairRunItems(pairId, runId, page, 25);
        if (!mounted) return;
        setRows((prev) => (page === 1 ? res.items : [...prev, ...res.items]));
        setLoadedTotal(res.total);
        setHasMore(page * res.limit < res.total);
      } catch {
        if (!mounted) return;
        if (page === 1) setRows([]);
        setHasMore(false);
      } finally {
        if (mounted) setFetching(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [pairId, runId, page, seededFromParent]);

  React.useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollRootRef.current;
    if (!node || !hasMore || fetching || seededFromParent) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPage((current) => current + 1);
        }
      },
      { root, rootMargin: "120px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, fetching, rows.length, seededFromParent]);

  const total = loadedTotal || itemTotal || rows.length;
  const title =
    total > rows.length
      ? `Questions & answers (showing ${rows.length} of ${total})`
      : `Questions & answers (${rows.length})`;
  const busy = loading || fetching;
  const selectedQuestion = selectedItem
    ? fullItemText(selectedItem.input, selectedItem.inputPreview)
    : "—";
  const selectedAnswer = selectedItem
    ? fullItemText(selectedItem.output, selectedItem.outputPreview)
    : "—";
  const selectedReferenceRaw = selectedItem
    ? fullItemText(selectedItem.expectedOutput, null, ["answer", "desired_answer", "text"])
    : "—";
  const caseBasis = selectedItem
    ? (selectedItem.evaluation?.basis
      ?? caseEvaluationBasis(selectedItem.expectedOutput))
    : null;
  const selectedReference =
    !selectedItem
      || caseBasis === "judge_only"
      || isSyntheticJudgeOnlyReference(selectedReferenceRaw)
      || selectedReferenceRaw === "—"
      ? "No verified reference"
      : selectedReferenceRaw;
  const selectedReason =
    selectedItem?.evaluation?.reason?.trim()
    || (selectedItem
      ? (
          selectedItem.scoreReasons?.["bank-faq-correctness"]?.trim()
          || selectedItem.scoreReasons?.["bank-faq-judge-only"]?.trim()
          || Object.entries(selectedItem.scoreReasons ?? {}).find(([name, reason]) =>
            /correctness|judge-only/i.test(name) && Boolean(reason?.trim()),
          )?.[1]?.trim()
        )
      : null)
    || "No evaluator reasoning was recorded.";

  return (
    <Card title={title} className={styles.fullWidthCard}>
      <div ref={scrollRootRef} className={styles.convoTableWrap}>
        <table className={styles.convoTable}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Question</th>
              <th>Answer</th>
              <th>Correctness</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row, index) => {
                const score = itemCorrectness(row, metric);
                const judgeOnlyScore = row.evaluation?.basis === "judge_only"
                  ? row.evaluation.score
                  : typeof row.scores["bank-faq-judge-only"] === "number"
                    ? row.scores["bank-faq-judge-only"]
                    : null;
                const unit =
                  judgeOnlyScore != null
                    ? toUnitScore(judgeOnlyScore)
                    : score == null
                    ? row.status === "failed"
                      ? 0
                      : null
                    : toUnitScore(score);
                const correctnessLabel =
                  unit == null
                    ? row.status === "completed"
                      ? "unscored"
                      : "—"
                    : judgeOnlyScore != null
                      ? `Judge-only ${unit.toFixed(3)}`
                      : unit.toFixed(3);
                return (
                  <tr
                    key={`${row.itemIndex}-${row.traceId ?? "local"}`}
                    className={styles.convoRowFadeIn}
                    style={{ animationDelay: `${Math.min(index, 12) * 20}ms` }}
                  >
                    <td>{row.itemIndex + 1}</td>
                    <td className={styles.cellTruncateWide}>{maskExplicitText(row.inputPreview)}</td>
                    <td className={styles.cellTruncateWide}>{maskExplicitText(row.outputPreview)}</td>
                    <td>{correctnessLabel}</td>
                    <td className={styles.convoActionsCell}>
                      <button
                        type="button"
                        className={styles.itemDetailsButton}
                        onClick={() => setSelectedItem(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : busy ? (
              <tr>
                <td colSpan={5} className={styles.runModalLine}>
                  Loading questions...
                </td>
              </tr>
            ) : (
              <tr>
                <td colSpan={5} className={styles.runModalLine}>
                  {runId ? "No items." : "Select a run to view Q&A."}
                </td>
              </tr>
            )}
            {hasMore ? (
              <tr ref={sentinelRef}>
                <td colSpan={5} className={styles.runModalLine}>
                  {fetching ? "Loading more..." : ""}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {selectedItem ? (
        <div
          className={styles.trendModalBackdrop}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedItem(null);
          }}
        >
          <div
            className={`${styles.trendModal} ${styles.evaluationModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="evaluation-detail-title"
          >
            <div className={styles.trendModalHeader}>
              <div>
                <h2 id="evaluation-detail-title">Evaluation details</h2>
                <p className={styles.evaluationMeta}>
                  {selectedItem.evaluation?.basis === "judge_only"
                    ? "Judge-only quality score — excluded from verified accuracy"
                    : selectedItem.evaluation?.basis === "verified_reference"
                      ? "Verified-reference accuracy"
                      : caseBasis === "judge_only"
                        || isSyntheticJudgeOnlyReference(selectedReferenceRaw)
                        ? "Judge-only case — no verified reference (greeting / fallback prompts)"
                        : "Legacy evaluation — basis was not recorded"}
                </p>
              </div>
              <button
                ref={evaluationCloseRef}
                type="button"
                className={styles.trendModalClose}
                onClick={() => setSelectedItem(null)}
                aria-label="Close evaluation details"
              >
                ×
              </button>
            </div>
            <div className={styles.evaluationModalBody}>
              <EvaluationTextSection title="Question" value={selectedQuestion} />
              <EvaluationTextSection title="Model answer" value={selectedAnswer} />
              <EvaluationTextSection title="Reference answer" value={selectedReference} />
              <EvaluationTextSection title="Evaluator reasoning" value={selectedReason} />
              {selectedItem.evaluation ? (
                <>
                  <dl className={styles.evaluationFacts}>
                    <div><dt>Score</dt><dd>{selectedItem.evaluation.score.toFixed(3)}</dd></div>
                    <div><dt>Judge</dt><dd>{selectedItem.evaluation.judgeModel}</dd></div>
                    <div><dt>Reference source</dt><dd>{selectedItem.evaluation.referenceSource ?? "None"}</dd></div>
                    <div><dt>Prompt version</dt><dd>{selectedItem.evaluation.promptVersion}</dd></div>
                  </dl>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
};

type LangfuseMetricsChartsProps = {
  trendDashboard: PairDashboardPayload | null;
  runDashboard: PairDashboardPayload | null;
  comparisons?: ModelComparisonGroup[];
  loading?: boolean;
  runLoading?: boolean;
  pairId?: string;
  selectedAgentId?: string | null;
  selectedRunId?: string | null;
  onSelectRun?: (agentId: string, runId: string) => void;
};

const LangfuseMetricsCharts: React.FC<LangfuseMetricsChartsProps> = ({
  trendDashboard,
  runDashboard,
  comparisons = [],
  loading,
  runLoading = false,
  pairId = "",
  selectedAgentId = null,
  selectedRunId = null,
  onSelectRun,
}) => {
  if (loading && !trendDashboard) {
    return (
      <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
        <div className={styles.dashboardLoadingSpinner} />
        <p className={styles.dashboardLoadingText}>Loading charts...</p>
      </section>
    );
  }

  if (
    !trendDashboard ||
    (trendDashboard.modelTrends.length === 0 &&
      trendDashboard.experimentScores.length === 0 &&
      Object.keys(trendDashboard.aggregates).length === 0)
  ) {
    return (
      <section className={styles.dashboardEmpty}>
        <p className={styles.dashboardEmptyText}>
          No Langfuse experiment scores for this dataset yet.
        </p>
      </section>
    );
  }

  const runPanels = runDashboard;
  const hasRunItems = (runPanels?.items.length ?? 0) > 0;
  const panelsPending = runLoading && !hasRunItems;

  return (
    <div className={styles.chartsArea}>
      <div className={styles.chartsTopGrid}>
        <CorrectnessTrendByModelCard
          dashboard={trendDashboard}
          selectedAgentId={selectedAgentId}
          selectedRunId={selectedRunId}
          onSelectRun={onSelectRun}
        />
        <CorrectnessGaugeCard
          dashboard={runPanels ?? trendDashboard}
          loading={runLoading && !runPanels}
        />
        <CostPerRunCard
          pairId={pairId}
          runId={selectedRunId}
          initialCostUsd={runPanels?.costUsd ?? null}
          loading={runLoading && runPanels?.costUsd == null}
        />
      </div>
      <div className={styles.chartsBottomGrid}>
        <DetailsCard dashboard={runPanels ?? trendDashboard} loading={runLoading && !runPanels} />
        <RunItemScoresCard
          pairId={pairId}
          runId={selectedRunId}
          correctnessMetric={runPanels?.correctnessMetric ?? trendDashboard.correctnessMetric}
          datasetName={runPanels?.datasetName ?? trendDashboard.datasetName}
          initialItems={runPanels?.items ?? EMPTY_DASHBOARD_ITEMS}
          loading={panelsPending}
        />
      </div>
      <div className={styles.chartsFullRow}>
        <ModelComparisonCard comparisons={comparisons} />
      </div>
      <div className={styles.chartsFullRow}>
        <RunItemsCard
          pairId={pairId}
          runId={selectedRunId}
          correctnessMetric={runPanels?.correctnessMetric ?? trendDashboard.correctnessMetric}
          itemTotal={runPanels?.itemTotal || runPanels?.traceCount || 0}
          initialItems={runPanels?.items ?? EMPTY_DASHBOARD_ITEMS}
          loading={panelsPending}
        />
      </div>
    </div>
  );
};

export default LangfuseMetricsCharts;
